import { COCSubCategory } from "../constants/License";

/**
 * 海技免状の履歴限定計算サービス
 */
const LicenseCalculationService = (() => {
  /**
   * 履歴限定の内容と解除に必要な乗船履歴を取得
   * @param {string} subCategory - 小区分（NAVIGATION, ENGINE, COMMUNICATION, ELECTRONIC_COMMUNICATION）
   * @param {string|number} code - 海技免状コード（13桁の数字）
   * @param {number} grade - 級（1-6）
   * @returns {Object} {
   *   restrictionContent: string[], // 履歴限定の内容
   *   requiredVoyageHistory: string, // 履歴限定解除に必要な乗船履歴
   * }
   */
  const getHistoryRestriction = (subCategory, code, grade) => {
    // 無効な入力のチェック
    if (!subCategory || code === undefined || code === null || !grade) {
      return {
        restrictionContent: null,
        requiredVoyageHistory: null,
      };
    }

    // コードを文字列に変換
    const codeStr = String(code);

    // 13桁全て入力されているかチェック
    if (codeStr.length !== 13) {
      return {
        restrictionContent: null,
        requiredVoyageHistory: null,
      };
    }

    // 数字のみかチェック
    if (!/^\d+$/.test(codeStr)) {
      return {
        restrictionContent: null,
        requiredVoyageHistory: null,
      };
    }

    // 4桁目（インデックス3）を抽出
    const fourthDigit = parseInt(codeStr[3], 10);

    // 航海（甲板部）の場合
    if (subCategory === COCSubCategory[1]) {
      return getNavigationRestriction(fourthDigit, grade);
    }

    // 機関の場合
    if (subCategory === COCSubCategory[2]) {
      return getEngineRestriction(fourthDigit, grade);
    }

    // 通信・電子通信の場合は履歴限定なし（PDFに記載なし）
    return {
      restrictionContent: null,
      requiredVoyageHistory: null,
    };
  };

  /**
   * 航海（甲板部）の履歴限定を取得
   * @param {number} code - コード（0, 1, 2, 6）
   * @param {number} grade - 級（1-6）
   * @returns {Object}
   */
  const getNavigationRestriction = (code, grade) => {
    switch (code) {
      case 1:
        return {
          restrictionContent: ["沿海（丙）区域以上200総トン以上の船長"],
          requiredVoyageHistory:
            "航海士として3年 または 船長（機関長）または一等航海士（一等機関士）として1年以上の乗船履歴を有し、かつ船舶職員履歴の全てを合計して2年以上の乗船履歴がある場合は特例解除可。",
        };

      case 2:
        if (grade == 5) {
          return {
            restrictionContent: [
              "沿海（丙）区域以上200総トン以上の船長",
              "沿海（丙）区域以上1600総トン以上の一等航海士",
            ],
            requiredVoyageHistory: "航海士として1年",
          };
        } else {
          return {
            restrictionContent: [
              "遠洋（甲）区域200総トン未満の船長",
              "沿海（丙）区域以上200総トン以上の船長",
              "沿海（丙）区域以上1600総トン以上の一等航海士",
            ],
            requiredVoyageHistory: "航海士として1年",
          };
        }

      case 6:
        return {
          restrictionContent: [
            "近海（乙）区域以上の船長および航海士",
            "沿海（丙）区域（国際航海）の船長および航海士",
            "沿海（丙）区域（非国際航海）、200総トン以上の船長および航海士",
          ],
          requiredVoyageHistory: "甲板部員として3カ月",
        };

      default:
        return {
          restrictionContent: null,
          requiredVoyageHistory: null,
        };
    }
  };

  /**
   * 機関の履歴限定を取得
   * @param {number} code - コード（0, 1, 2, 4, 6）
   * @param {number} grade - 級（1-6）
   * @returns {Object}
   */
  const getEngineRestriction = (code, grade) => {
    switch (code) {
      case 4:
        return {
          restrictionContent: ["沿海（丙）区域以上3000kw以上の機関長"],
          requiredVoyageHistory:
            "機関士として3年 または 船長（機関長）または一等航海士（一等機関士）として1年以上の乗船履歴を有し、かつ船舶職員履歴の全てを合計して2年以上の乗船履歴がある場合は特例解除可。平成26年4月1日以前に海技士（機関）の免状を受有している場合、平成28年中までに申請すれば「機関士として2年」の履歴で全ての履歴限定を解除可能（平成26年4月1日以降進級者は除く）。",
        };

      case 1:
        return {
          restrictionContent: [
            "近海（乙）区域以上750kw以上3000kw未満の機関長",
            "沿海（丙）区域以上3000kw以上の機関長",
          ],
          requiredVoyageHistory: "機関士として2年",
        };

      case 2:
        if (grade == 5) {
          return {
            restrictionContent: [
              "近海（乙）区域以上750kw以上の一等機関士",
              "沿海（丙）区域以上3000kw以上の一等機関士",
            ],
            requiredVoyageHistory: "機関士として1年",
          };
        } else {
          return {
            restrictionContent: [
              "近海（乙）区域以上750kw以上3000kw未満の機関長および一等機関士",
              "沿海（丙）区域以上3000kw以上の機関長および一等機関士",
            ],
            requiredVoyageHistory: "機関士として1年",
          };
        }

      case 6:
        return {
          restrictionContent: [
            "沿海（丙）区域以上750kw以上の機関長および機関士",
          ],
          requiredVoyageHistory: "機関部員として3ヶ月",
        };

      default:
        return {
          restrictionContent: null,
          requiredVoyageHistory: null,
        };
    }
  };

  return {
    getHistoryRestriction,
  };
})();

export default LicenseCalculationService;
